/* eslint-disable react/jsx-key */

/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useEffect, useContext, useState, useRef, useMemo } from 'react'
import { Button, Input, Empty, message, DatePicker } from 'antd'
import { UnitSelection, getApiTargetInfo, ExportComponent } from 'core'
import { connect } from 'react-redux'
import R from 'ramda'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
  isValidYearRange,
} from 'core/utils'

import {
  CriteriaRow,
  Title,
  Content,
  FullHeightSizer,
  Filler,
} from 'core/screen-util'
import { strCol, numCol, intCol } from '@integec/grid-tools/lib/cols'
import {
  modifierCol,
  modifiedDateCol,
  utilityReqHeader,
  unitHeader,
} from 'core/HeaderColTypeProps'

import {
  UnitSelectionContext,
  DatePickerContext,
  SpinContext,
  ErrorContext,
} from 'context'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getUtilityUnitsMap,
  getPermitActions,
  getEnvConfig,
  isAdminType,
  getValidYearRange,
} from 'core/selectors'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import {
  getFuelCodeInfo,
  getCard99,
  /* getUtilityUnitMap, */
  getCard99Extend,
  lockCard99Data,
} from 'api/pgads'
import Grid, {
  virtualizedGridRenderer,
  defaultVirtualizedCellRender,
} from '@integec/grid-tools'
import Card99EditDialog from './component/card99/Card99EditAntd'
import CardsErrorDialog from './CardErrorDialogAntd'
import { LockCheckFunc } from './Card95Antd'
import * as ScreenPermissions from 'core/EventActions'

const Card99Antd = ({
  apiTargetInfo,
  utilityNameMap,
  unitNameMap,
  utilityUnitMap,
  actionPermits,
  envConfig,
  isAdmin,
  ...props
}) => {
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  // const [loading, setLoading] = useState(false)
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  // const [err, setError] = useState(null)
  const [data, setData] = useState(null)
  // const  [editInfo, setEditInfo] = useState(freeEditEngine.generateInitialEditInfo())
  const [selectedRows, setRowsSelected] = useState([])
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [mode, setMode] = useState()

  const [dataErrors, setDataErrors] = useState(null)
  const { selectedUnits, setVisibleUnitSelection } = useContext(
    UnitSelectionContext
  )
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  // const [dataSelOperationPopup, setdataSelOperationPopup] = useState()
  const [card99Extend, setCard99Extend] = useState(false)
  const [fuelCodeMap, setFuelCodeMap] = useState([])
  const [fuelBurnedMap, setFuelBurnedMap] = useState([])
  const [avgHeatMap, setAvgHeatMap] = useState([])
  useEffect(() => {
    setData(null)
    setRowsSelected([])
  }, [selectedDateRangePicker])

  const [lockData, setLockData] = useState([])

  useEffect(
    _ => {
      if (selectedUnits.length <= 0) return
      const isValid = isValidYearRange(
        props.validYearRange,
        selectedDateRangePicker[0],
        selectedDateRangePicker[1]
      )
      if (!isValid) return
      lockCard99Data(apiTargetInfo)({
        year: selectedDateRangePicker[0].format('YYYY'),
        month: selectedDateRangePicker[0].format('MM'),
        units: selectedUnits.map(u => u.unitId),
        endYear: selectedDateRangePicker[1].format('YYYY'),
        endMonth: selectedDateRangePicker[1].format('MM'),
      }).fork(setError, setLockData)
    },
    [
      apiTargetInfo,
      selectedUnits,
      setError,
      setSelectedDateRagePicker,
      selectedDateRangePicker,
      props.validYearRange,
    ]
  )

  const cardFreezeAccess = useMemo(
    _ =>
      hasAccesPermits({
        actionPermits,
        eventActions: [ScreenPermissions.CARD_FREEZE],
        matchAny: true,
      }),
    [actionPermits]
  )

  const conditionalHeader = useMemo(() => {
    return cardFreezeAccess ? freezeHeaders : headers
  }, [cardFreezeAccess])

  const setRowsSelectedWithLock = rowData => {
    const data = rowData[0]
    const lockStatus = LockCheckFunc(lockData, data || {}) || {}

    setRowsSelected([{ ...data, lock: lockStatus.lock || 'U' }])
  }

  useEffect(() => {
    getCard99Extend(apiTargetInfo).fork(setError, card99Extend => {
      setCard99Extend(card99Extend !== '0')
    })
    getFuelCodeInfo(apiTargetInfo).fork(setError, fuelCodes => {
      //   this.setState({
      //     fuelCodeMap: fuelCodeList(fuelCodes),
      //     fuelBurnedMap: fuelBurnedList(fuelCodes),
      //     avgHeatMap: avgHeatList(fuelCodes),
      //   })
      setFuelCodeMap(fuelCodeList(fuelCodes))
      setFuelBurnedMap(fuelBurnedList(fuelCodes))
      setAvgHeatMap(avgHeatList(fuelCodes))
    })
  }, [apiTargetInfo, setError])

  const headerWithFormatter = headers =>
    headers.map(header =>
      header.ident === 'utility'
        ? {
            ...header,
            // ...toSelectionColProps(utilityNameMap),
            dataFormatter: ({ value }) => utilityNameMap[value] || value,
            sortIndexGetter: ({ rowData }) =>
              utilityNameMap[rowData.utility] || rowData.utility,
          }
        : header.ident === 'unit'
        ? {
            ...header,
            // choices: [],
            dataFormatter: ({ value }) => unitNameMap[value] || value,
            sortIndexGetter: ({ rowData }) =>
              unitNameMap[rowData.unit] || rowData.unit,
          }
        : header.ident === 'pFuel' ||
          header.ident === 'sFuel' ||
          header.ident === 'tFuel' ||
          header.ident === 'qFuel'
        ? {
            ...header,
            ...toSelectionColProps(fuelCodeMap),
            dataFormatter: ({ value }) => fuelCodeMap[value] || value,
          }
        : header
    )

  const onRetrieve = ({ selUnits = selectedUnits }) => {
    const isValid = isValidYearRange(
      props.validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
    if (!isValid) return
    if (selUnits.length <= 0) {
      message.error('Please, Select Units')
      setVisibleUnitSelection(true)
      return
    }
    setSpinning(true)
    getCard99(apiTargetInfo)(
      selectedDateRangePicker[0].format('YYYY'),
      selectedDateRangePicker[0].format('MM'),
      selUnits[0].utility,
      selUnits.map(u => u.unitId),
      selectedDateRangePicker[1].format('YYYY'),
      selectedDateRangePicker[1].format('MM')
    ).fork(setError, data => {
      setSpinning(false)
      setData(data)
      setRowsSelected([])
    })
  }
  const onInsert = _ => {
    setMode('N')
    setShowEditDialog(true)
  }
  const doDelete = _ => {
    // setSpinning(true)
    setMode('D')
    setShowEditDialog(true)
  }

  const onView = _ => {
    setMode('V')
    setShowEditDialog(true)
  }

  const onUpdate = _ => {
    setMode('E')
    setShowEditDialog(true)
  }

  const cellRenderer = props =>
    defaultVirtualizedCellRender({
      ...props,
      onDoubleClick: () => {
        // setMode('E')
        // setShowEditDialog(true)
        onView()
      },
    })

  const GridRef = useRef()

  return [
    dataErrors && (
      <CardsErrorDialog
        keys="card95errordialog"
        errors={dataErrors}
        closeErrorPrompt={setDataErrors}
        action={mode}
        visible={dataErrors !== undefined}
        setShowEditDialog={setShowEditDialog}
        retrieveData={onRetrieve}
      />
    ),
    <>
      {showEditDialog && (
        <Card99EditDialog
          isAdmin={isAdmin}
          setDataErrors={setDataErrors}
          setShowEditDialog={setShowEditDialog}
          showEditDialog={showEditDialog}
          key="card99editprompt"
          mode={mode}
          targetInfo={apiTargetInfo}
          utilityNameMap={utilityNameMap}
          utilityUnitMap={utilityUnitMap}
          unitNameMap={unitNameMap}
          // criteriaMonth={parseInt(selectedDateRangePicker[0].format('MM'))}
          // criteriaYear={parseInt(selectedDateRangePicker[0].format('YYYY'))}
          // criteriaUtility={selectedUnits[0] ? selectedUnits[0].utility : ''}
          // criteriaUnit={selectedUnits[0] ? selectedUnits[0] : ''}
          fuelCodeMap={fuelCodeMap}
          fuelBurnedMap={fuelBurnedMap}
          avgHeatMap={avgHeatMap}
          headers={headers}
          card99Extend={card99Extend}
          rowData={
            mode === 'N'
              ? {
                  // utility: selectedUnits[0].utility,
                  // unit: selectedUnits[0].utility + selectedUnits[0].unit,
                  utility:
                    selectedUnits.length === 1
                      ? selectedUnits[0].utility
                      : null,
                  unit:
                    selectedUnits.length === 1
                      ? selectedUnits[0].utility + selectedUnits[0].unit
                      : null,
                  month: parseInt(selectedDateRangePicker[0].format('MM')),
                  year: parseInt(selectedDateRangePicker[0].format('YYYY')),
                }
              : selectedRows[0]
          }
          setMode={setMode}
          retrieveData={onRetrieve}
          setError={setError}
          setLoading={setSpinning}
          spinning={spinning}
        />
      )}
      <div
        style={{
          backgroundColor: '#f0f2f5',
          marginLeft: '20px',
          marginRight: '20px',
        }}
      >
        <UnitSelection
          setData={() => setData(null)}
          accesskey="u"
          retrieveData={onRetrieve}
          //  renderContent={renderUnitContent}
        />

        <CriteriaRow>
          <div
            style={{
              display: 'flex',
              //  justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Title
              style={{
                marginRight: '0px',
                marginBottom: '5px',
                minWidth: '170px',
              }}
            >
              {title}
            </Title>

            <DatePicker.RangePicker
              allowClear={false}
              mode={['month', 'month']}
              // mode="decade"
              format="MMMM-YYYY"
              value={selectedDateRangePicker}
              placeholder={['Start month', 'End month']}
              // open={openPicker}
              onChange={dates => {
                setSelectedDateRagePicker(dates)
              }}
              onPanelChange={dates => {
                setSelectedDateRagePicker(dates)
              }}
              css={{ marginLeft: '10px' }}
            />
            {/* <Button onClick={e => setdataSelOperationPopup(true)}>Operation</Button> */}
            <Button
              onClick={onRetrieve}
              type="primary"
              icon="database"
              css={{ marginLeft: '10px' }}
            >
              Retrieve
            </Button>
            {/* {hasAccesPermits({
              authGodMode: envConfig.authGodMode,
              actionPermits,
              eventActions: [ScreenPermissions.CARD99_VIEWER],
              matchAny: true,
            }) && (
              <Button onClick={onView} icon="plus-circle" style={{ backgroundColor: '#e6f7ff' }}>
                View Data
              </Button>
            )} */}

            {hasAccesPermits({
              authGodMode: envConfig.authGodMode,
              actionPermits,
              eventActions: [ScreenPermissions.CARD99_ADMIN],
              matchAny: true,
            }) && (
              <>
                <Button
                  onClick={onInsert}
                  icon="plus-circle"
                  style={{ backgroundColor: '#e6f7ff', marginLeft: '10px' }}
                >
                  Insert
                </Button>
                <Button
                  onClick={onUpdate}
                  icon="edit"
                  type="primary"
                  disabled={selectedRows.length <= 0}
                  css={{ marginLeft: '10px' }}
                >
                  Update
                </Button>
                <Button
                  onClick={doDelete}
                  type="danger"
                  disabled={selectedRows.length <= 0}
                  icon="delete"
                  css={{ marginLeft: '10px' }}
                >
                  Delete
                </Button>
              </>
            )}
            <Filler />
            <Input.Search
              placeholder="Filter"
              value={fuzzyFilter}
              css={{
                marginLeft: '10px',
                marginRight: '10px',
                flex: '0 0 140px',
              }}
              onChange={e => setFuzzyFilter(e.target.value)}
            />

            <ExportComponent
              getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
              data={data}
              headers={headerWithFormatter(conditionalHeader)}
              fileName={title}
              btnStyle={{ marginRight: '10px' }}
              disabled={!!(data && data.length <= 0)}
            />
          </div>
        </CriteriaRow>
        <FullHeightSizer heightOffset={280}>
          {({ height, width }) =>
            data && data.length > 0 ? (
              <Content style={{ height: height - 20 }}>
                <Grid
                  ref={GridRef}
                  sortEnabled
                  data={data}
                  headers={headerWithFormatter(conditionalHeader)}
                  altBy={u => u.utility}
                  altBgColor="#ebebeb"
                  // onEditInfoChange={info => editRow(info)}
                  // editInfo={editInfo}
                  render={virtualizedGridRenderer({
                    autoFixColByKey: cardFreezeAccess,
                    rowHeaderRender,
                    height: height - 60,
                    width: width - 50,
                    cellRender: cellRenderer,
                  })}
                  fuzzyFilter={fuzzyFilter}
                  isEditable={false}
                  onSelectionChange={rows => {
                    setRowsSelectedWithLock(rows.selectedRows)
                  }}
                  selectionMode="single"
                  selectionType="row"
                  initialSortOptions={[
                    utilityReqHeader,
                    unitHeader,
                    { ident: 'year', sortOrder: 'asc' },
                    { ident: 'month', sortOrder: 'asc' },
                  ]}
                />
              </Content>
            ) : (
              <Content style={{ height: '200px' }}>
                <Empty
                  imageStyle={{ paddingTop: '30px' }}
                  description={
                    selectedUnits && selectedUnits.length <= 0
                      ? 'Please select units'
                      : data && data.length <= 0
                      ? 'No Data from Retrieve'
                      : 'Please click on retrieve button to retrieve data'
                  }
                />
              </Content>
            )
          }
        </FullHeightSizer>
      </div>
    </>,
  ]
}
const fuelCodeList = R.compose(
  R.fromPairs,
  R.map(a => [a.code, `${a.code}-${a.description}`])
)
const fuelBurnedList = R.compose(
  R.fromPairs,
  R.map(a => [a.code, a.unit99 ? a.unit99 : ''])
)
const avgHeatList = R.compose(
  R.fromPairs,
  R.map(a => [a.code, a.heatUnit99 ? a.heatUnit99 : ''])
)

const title = 'Card99 - Fuel Data'
const headers = [
  unitHeader,
  intCol({ ident: 'year', display: 'Year', isKey: true, width: 60 }),
  intCol({ ident: 'month', display: 'Month', isKey: true, width: 50 }),
  utilityReqHeader,
  strCol({ ident: 'pFuel', display: 'Primary Fuel', width: 180 }),
  numCol({ ident: 'pBurned', display: 'Primary Fuel Burned' }),
  numCol({ ident: 'pAvgHeat', display: 'Primary Avg Heat' }),
  numCol({ ident: 'pAsh', display: 'Primary Ash Content' }),
  numCol({ ident: 'pMoist', display: 'Primary Moisture Content' }),
  numCol({ ident: 'pSulfur', display: 'Primary Sulfur Content' }),
  strCol({
    ident: 'sFuel',
    display: 'Secondary Fuel',
    width: 180,
    isFiltered: true,
  }),
  numCol({ ident: 'sBurned', display: 'Secondary Fuel Burned' }),
  numCol({ ident: 'sAvgHeat', display: 'Secondary Avg Heat' }),
  numCol({ ident: 'sAsh', display: 'Secondary Ash Content' }),
  numCol({ ident: 'sMoist', display: 'Secondary Moisture Content' }),
  numCol({ ident: 'sSulfur', display: 'Secondary Sulfur Content' }),
  strCol({
    ident: 'tFuel',
    display: 'Tertiary Fuel',
    width: 180,
    isFiltered: true,
  }),
  numCol({ ident: 'tBurned', display: 'Tertiary Fuel Burned' }),
  numCol({ ident: 'tAvgHeat', display: 'Tertiary Avg Heat' }),
  numCol({ ident: 'tAsh', display: 'Tertiary Ash Content' }),
  numCol({ ident: 'tMoist', display: 'Tertiary Moisture Content' }),
  numCol({ ident: 'tSulfur', display: 'Tertiary Sulfur Content' }),
  strCol({
    ident: 'qFuel',
    display: 'Quaternary Fuel',
    width: 180,
    isFiltered: true,
  }),
  numCol({ ident: 'qBurned', display: 'Quaternary Fuel Burned' }),
  numCol({ ident: 'qAvgHeat', display: 'Quaternary Avg Heat' }),
  numCol({ ident: 'qAsh', display: 'Quaternary Ash Content' }),
  numCol({ ident: 'qMoist', display: 'Quaternary Moisture Content' }),
  numCol({ ident: 'qSulfur', display: 'Quaternary Sulfur Content' }),
  numCol({ ident: 'loiPercent', display: 'Loi Percent' }),
  strCol({ ident: 'notes', display: 'Notes', width: 200, alignment: 'left' }),
  modifierCol,
  modifiedDateCol,
].map(e => ({ ...e, isFiltered: true, ellipsis: true }))

const freezeHeaders = [
  unitHeader,
  intCol({ ident: 'year', display: 'Year', isKey: true, width: 60 }),
  intCol({ ident: 'month', display: 'Month', isKey: true, width: 50 }),
  strCol({
    ident: 'utility',
    display: 'Utility',
    width: 450,
    isRequired: true,
    // isKey: true,
    sortOrder: 'asc',
    ellipsis: true,
  }),
  strCol({ ident: 'pFuel', display: 'Primary Fuel', width: 180 }),
  numCol({ ident: 'pBurned', display: 'Primary Fuel Burned' }),
  numCol({ ident: 'pAvgHeat', display: 'Primary Avg Heat' }),
  numCol({ ident: 'pAsh', display: 'Primary Ash Content' }),
  numCol({ ident: 'pMoist', display: 'Primary Moisture Content' }),
  numCol({ ident: 'pSulfur', display: 'Primary Sulfur Content' }),
  strCol({
    ident: 'sFuel',
    display: 'Secondary Fuel',
    width: 180,
    isFiltered: true,
  }),
  numCol({ ident: 'sBurned', display: 'Secondary Fuel Burned' }),
  numCol({ ident: 'sAvgHeat', display: 'Secondary Avg Heat' }),
  numCol({ ident: 'sAsh', display: 'Secondary Ash Content' }),
  numCol({ ident: 'sMoist', display: 'Secondary Moisture Content' }),
  numCol({ ident: 'sSulfur', display: 'Secondary Sulfur Content' }),
  strCol({
    ident: 'tFuel',
    display: 'Tertiary Fuel',
    width: 180,
    isFiltered: true,
  }),
  numCol({ ident: 'tBurned', display: 'Tertiary Fuel Burned' }),
  numCol({ ident: 'tAvgHeat', display: 'Tertiary Avg Heat' }),
  numCol({ ident: 'tAsh', display: 'Tertiary Ash Content' }),
  numCol({ ident: 'tMoist', display: 'Tertiary Moisture Content' }),
  numCol({ ident: 'tSulfur', display: 'Tertiary Sulfur Content' }),
  strCol({
    ident: 'qFuel',
    display: 'Quaternary Fuel',
    width: 180,
    isFiltered: true,
  }),
  numCol({ ident: 'qBurned', display: 'Quaternary Fuel Burned' }),
  numCol({ ident: 'qAvgHeat', display: 'Quaternary Avg Heat' }),
  numCol({ ident: 'qAsh', display: 'Quaternary Ash Content' }),
  numCol({ ident: 'qMoist', display: 'Quaternary Moisture Content' }),
  numCol({ ident: 'qSulfur', display: 'Quaternary Sulfur Content' }),
  numCol({ ident: 'loiPercent', display: 'Loi Percent' }),
  strCol({ ident: 'notes', display: 'Notes', width: 200, alignment: 'left' }),
  modifierCol,
  modifiedDateCol,
].map(e => ({ ...e, isFiltered: true, ellipsis: true }))

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  utilityUnitMap: getUtilityUnitsMap(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
  isAdmin: isAdminType(state),
  validYearRange: getValidYearRange(state),
})

export default connect(mapStateToProps)(Card99Antd)
